<template>
  <div class="w-full">
    <label for="users_emails" class="block text-sm font-medium text-gray-700">
      Invite {{ userType }}s
    </label>
    <div class="students flex gap-x-2">
      <v-select
        :data-cy="`${userType}-invitation`"
        class="
          mt-1
          h-0
          block
          w-full
          border-gray-300
          focus:ring-vice-teal-500
          focus:border-vice-teal-500
          rounded-md
          shadow-sm
          sm:text-sm
          text-gray-900
        "
        id="users_emails"
        placeholder="Type or select email addresses to invite"
        ref="usersSelect"
        @search="onSearch"
        :disabled="!canAddUsers"
        label="email"
        :options="options"
        @option:selected="selectedOption"
        :create-option="onCreateOption"
        @keyup.enter="addMultipleUsers"
      >
        <template #option="option">
          <div
            v-if="option.isGroup"
            class="font-bold text-vice-teal-700 py-1"
            @mouseover="highlightedInGroup = option.name"
            @mouseleave="highlightedInGroup = null"
          >
            Group: {{ option.name }}
          </div>
          <div
            v-else
            :class="{
              'pl-4': option.groupName,
              'bg-vice-teal-150 rounded-md leading-5 border border-vice-teal-500':
                highlightedInGroup === option.groupName,
            }"
          >
            {{ option.email || option.name }}
          </div>
        </template>
        <template v-slot:no-options>
          <span v-if="searchMatchedWithSelected">This user has already been selected</span>
          <span v-else>
            We cannot find users within your organization with these emails. To add external
            participants, enter their full email address and click "Add"
          </span>
        </template>
        <template #open-indicator>
          <!-- Custom dropdown icon -->
          <span class="mr-1">
            <SelectDropdownArrow class="h-5 w-5 text-gray-400" aria-hidden="true" />
          </span>
        </template>
      </v-select>
      <div
        @click="addMultipleUsers"
        :class="{
          'opacity-50 cursor-not-allowed': !canAddUsers,
        }"
        class="
          rounded
          w-20
          cursor-pointer
          bg-vice-teal-400
          flex
          justify-center
          items-center
          text-sm text-white
          hover:bg-vice-teal-500
          add-button
        "
      >
        <!-- <PlusIcon class="w-5 pr-1" /> -->
        ADD
      </div>
    </div>
    <div class="my-2 border rounded-lg p-px shadow">
      <table class="table-fixed w-full divide-y divide-gray-200">
        <thead class="bg-gray-100">
          <tr>
            <th
              scope="col"
              class="
                w-1/4
                md:w-1/3
                whitespace-nowrap
                md:p-4
                p-2
                text-left text-xxs
                font-medium
                text-gray-500
                tracking-wider
              "
            >
              Email
            </th>
            <th
              scope="col"
              class="
                w-1/4
                whitespace-nowrap
                md:p-4
                p-2
                text-left text-xxs
                font-medium
                text-gray-500
                tracking-wider
              "
            >
              Invite status
            </th>
            <th
              scope="col"
              class="
                w-1/5
                whitespace-nowrap
                md:p-4
                p-2
                text-right text-xxs
                font-medium
                text-gray-500
                tracking-wider
              "
            >
              Options
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(invitedUser, index) in selectedOptions"
            :key="invitedUser.email"
            :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
          >
            <td class="px-2 md:px-4 py-1 md:w-2/3 w-full text-left">
              <div class="font-medium text-gray-600 text-xxs md:text-xs break-words">
                {{ invitedUser.email }}
              </div>
            </td>
            <td class="px-0 md:px-4 py-1 w-1/4 whitespace-nowrap">
              <span class="flex gap-1 items-center" v-if="invitedUser.accepted_at">
                <ViceBadge green small>Already accepted</ViceBadge>
              </span>
              <span class="flex gap-1 items-center" v-else-if="!invitedUser.email_sent">
                <ViceBadge indigo small>Sending email</ViceBadge>
              </span>
              <span class="flex gap-1 items-center" v-else>
                <ViceBadge yellow small>Invitation sent</ViceBadge>
              </span>
            </td>

            <td class="px-4 py-1 w-1/4 whitespace-nowrap text-gray-500">
              <a
                href="javascript:void(0)"
                :data-cy="`remove-${userType}`"
                @click="removeInvite(invitedUser)"
              >
                <ViceButton small outline red icon="UserRemoveIcon">Remove</ViceButton>
              </a>
            </td>
          </tr>

          <tr v-if="!selectedOptions.length">
            <td colspan="3" class="p-4 w-2/3 whitespace-nowrap text-center">
              <span class="text-xs text-gray-500">No {{ userType }}s invited yet.</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Upload CSV -->
    <button
      type="button"
      class="
        cursor-pointer
        relative
        flex
        justify-center
        items-center
        flex-col
        w-full
        border-2 border-gray-300 border-dashed
        rounded-md
        p-5
        text-center
        hover:border-gray-400
        group
      "
    >
      <div class="flex items-center justify-center">
        <div class="flex space-x-2">
          <div class="flex items-center">
            <UploadIcon class="w-7 text-gray-300 group-hover:text-gray-400" />
          </div>
          <div class="flex items-center">
            <div class="text-left">
              <span class="text-sm font-medium text-gray-700 group-hover:text-gray-500 block">
                <span class="font-bold">
                  <span class="text-vice-teal-700 underline">Click to upload</span>
                  or drag and drop
                </span>
                <br />
                to invite multiple participants
              </span>

              <span class="mt-2 text-sm font-medium text-gray-500 group-hover:text-gray-400 block">
                CSV (max. 3MB)
              </span>
            </div>
          </div>
        </div>
      </div>
      <input
        :data-cy="`csv-${userType}-invitation`"
        type="file"
        ref="csvFileInput"
        multiple
        accept=".csv"
        @change="onCsvFileUpload"
        class="cursor-pointer opacity-0 absolute h-full w-full"
      />
    </button>

    <a
      data-cy="download-sample"
      href="/user_emails.csv"
      download="user_emails.csv"
      class="text-vice-teal-600 underline text-sm font-bold block mt-3"
      v-if="userType == 'participant' || userType == 'student'"
    >
      Download .csv sample
    </a>
    <br />
    <span class="text-red-500 text-sm break-all" v-if="error">{{ error }}</span>
  </div>
</template>

<script>
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import Papa from 'papaparse';
import axios from 'axios';
import _ from 'lodash';
import UploadIcon from '@/Components/Icons/UploadIcon.vue';
import ViceButton from '@/Components/Buttons/ViceButton.vue';
import ViceBadge from '@/Components/Badges/ViceBadge.vue';
import SelectDropdownArrow from '@/Components/Icons/SelectDropdownArrow.vue';

export default {
  name: 'UserInvitationList',
  components: {
    ViceBadge,
    ViceButton,
    vSelect,
    UploadIcon,
    SelectDropdownArrow,
  },
  props: {
    userType: {
      type: String,
      required: true,
    },
    maxAmount: {
      type: Number,
      default: 9999,
    },
    takenUsers: {
      type: Array,
      default: () => [],
    },
    availableUsers: {
      type: Array,
      required: true,
    },
    initialSelected: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      allOptions: [],
      selectedOptions: [],
      error: '',
      extractedEmailsList: [],
      searchMatchedWithSelected: false,
      highlightedInGroup: null,
    };
  },
  computed: {
    errorMessages() {
      return {
        maxExceeded: `E-mail invitations exceed the number of ${this.userType}s set.`,
        alreadyAdded: 'E-mail is already added.',
      };
    },
    canAddUsers() {
      return this.selectedOptions.length < this.maxAmount;
    },
    options() {
      // Group users by group name if they have groups
      const groupsMap = {};
      const usersWithoutGroups = [];

      this.availableUsers.forEach((user) => {
        const isSelected = this.selectedOptions.some((_selected) => _selected.email === user.email);
        const isTaken = this.takenUsers?.some((email) => email === user.email);

        if (isSelected || isTaken) {
          return;
        }

        if (user.groups && user.groups.length > 0) {
          user.groups.forEach((group) => {
            if (!groupsMap[group.name]) {
              groupsMap[group.name] = {
                id: `group-${group.id}`,
                name: group.name,
                isGroup: true,
                students: [],
              };
            }
            groupsMap[group.name].students.push(user);
          });
        } else if (!user?.isGroup) usersWithoutGroups.push(user);
      });

      const options = [];

      Object.values(groupsMap).forEach((group) => {
        if (group.students.length > 0) {
          options.push(group);
          group.students.forEach((s) => {
            options.push({
              ...s,
              groupName: group.name,
            });
          });
        }
      });

      usersWithoutGroups.forEach((s) => {
        options.push(s);
      });

      return options;
    },
  },

  watch: {
    selectedOptions: {
      deep: true,
      handler() {
        this.$emit('update:selected', this.selectedOptions);
      },
    },
    initialSelected: {
      immediate: true,
      handler(val) {
        if (val && val.length > 0 && this.selectedOptions.length === 0) {
          this.selectedOptions = JSON.parse(JSON.stringify(val));
        }
      },
    },
  },

  methods: {
    addMultipleUsers() {
      if (this.extractedEmailsList.length + this.selectedOptions.length > this.maxAmount) {
        this.error = this.errorMessages.maxExceeded;
        return;
      }
      this.extractedEmailsList.forEach((email) => {
        if (
          !this.selectedOptions.some((s) => s.email === email) &&
          !this.takenUsers.includes(email)
        ) {
          this.selectedOptions.push({ email, email_sent: false });
          this.$refs.usersSelect.search = '';
        } else {
          this.error = this.errorMessages.alreadyAdded;
        }
      });
    },
    onSearch(search, loading) {
      this.searchMatchedWithSelected = this.selectedOptions.some(({ email }) => email === search);
      this.error = '';
      if (!search.length) return;
      this.extractedEmailsList = _.uniq(
        search.split(/[\s,;]+/).filter((email) => this.validateEmail(email)),
      );
      loading(true);
      this.search(loading, search, this);
    },

    search: _.debounce((loading, search, vm) => {
      axios.post(route('user.emails.list'), { query: search }).then((response) => {
        vm.allOptions = response.data.map((email) => ({ email, email_sent: false }));
        loading(false);
      });
    }, 350),

    // eslint-disable-next-line consistent-return
    onCreateOption(email) {
      if (this.validateEmail(email)) {
        return { email, email_sent: false };
      }
    },

    removeInvite(invitedUser) {
      this.selectedOptions = this.selectedOptions.filter(
        (user) => user.email !== invitedUser.email,
      );
    },

    validateEmail(email) {
      return Boolean(
        email.match(
          // eslint-disable-next-line no-useless-escape
          /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        ),
      );
    },

    selectedOption(option) {
      if (option.isGroup) {
        option.students.forEach((student) => {
          if (!this.selectedOptions.some((s) => s.email === student.email)) {
            if (this.canAddUsers) {
              this.selectedOptions.push({
                ...student,
                email: student.email.toLowerCase(),
                email_sent: student.email_sent || false,
              });
            } else {
              this.error = this.errorMessages.maxExceeded;
            }
          }
        });
        this.$refs.usersSelect.clearSelection();
        return;
      }

      const value = { ...option };
      value.email = value.email.toLowerCase();
      if (this.validateEmail(value.email)) {
        const index = this.selectedOptions.findIndex(({ email }) => email === value.email);
        if (index >= 0) {
          // this.selectedOptions.splice(index, 1); // Maybe don't toggle on select, just add
        } else if (this.canAddUsers) {
          this.selectedOptions.push({
            ...value,
            email: value.email,
            email_sent: value.email_sent || false,
          });
        } else {
          this.error = this.errorMessages.maxExceeded;
        }
        this.$refs.usersSelect.clearSelection();
      }
    },

    onCsvFileUpload(e) {
      const self = this;
      Papa.parse(e.target.files[0], {
        delimiter: ',',
        header: true,
        skipEmptyLines: true,
        complete(results) {
          if (results.errors.length) {
            self.error = results.errors[0].message;
          } else {
            self.tidyCsvData(results.data);
          }
        },
      });
      this.$refs.csvFileInput.value = null;
    },

    tidyCsvData(csvData) {
      const invalidEmails = [];
      if (csvData.length + this.selectedOptions.length > this.maxAmount) {
        this.error = this.errorMessages.maxExceeded;
        return;
      }
      this.error = '';
      csvData.forEach((row) => {
        if (!row.email) return;
        row.email = row.email.replace(/ /g, '').toLowerCase();
        if (this.validateEmail(row.email)) {
          if (
            !this.selectedOptions.some((s) => s.email === row.email) &&
            !this.takenUsers?.some((takenEmail) => takenEmail === row.email)
          ) {
            this.selectedOption(row);
          }
        } else {
          invalidEmails.push(row);
          this.error = `Invalid email: ${invalidEmails.map((email) => email.email)}`;
        }
      });
    },
  },
};
</script>

<style>
::placeholder {
  color: rgb(136, 139, 136) !important;
  font-size: 14px;
}

.vs__search,
.vs__dropdown-toggle,
.vs__selected-options {
  background: none;
}

.vs__search {
  width: 100%;
  margin-top: 0.25rem;
  border-radius: 0.375rem;
  --tw-border-opacity: 1;
}

.vs__actions {
  cursor: pointer;
}

.vs__dropdown-menu {
  background: #ffffff;
  color: var(--ViCE---Dark-Grey, #606367);
  font-size: 14px;
  & .vs__dropdown-option--highlight {
    background-color: rgba(45, 212, 191, var(--tw-bg-opacity));
  }

  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 6px 0 6px 0;
  margin-top: 2px;
  position: relative;
  z-index: 9999;
  opacity: 95%;
}

.vs__dropdown-menu .vs__dropdown-option {
  padding: 0.25rem 1rem;
  border-radius: 6px;
  margin: 0 6px;
  cursor: pointer;
}

.bg-vice-teal-150 {
  background-color: #e6fcf5;
}

:deep(.vs--searchable .vs__dropdown-toggle) {
  width: 100%;
  border-radius: 0.375rem;
  --tw-border-opacity: 1;
  height: 38px;
}

:deep(.vs--open .vs__dropdown-toggle) {
  border-bottom-color: #d1d5db;
  font-size: 14px;
  color: rgb(136, 139, 136) !important;
}

.add-button {
  height: 38px;
  margin-top: 4px;
}
::placeholder {
  color: rgb(136, 139, 136) !important;
  font-size: 14px;
}

.vs__search,
.vs__dropdown-toggle,
.vs__selected-options {
  background: none;
}

.vs__search {
  width: 100%;
  margin-top: 0.25rem;
  border-radius: 0.375rem;
  --tw-border-opacity: 1;
}

.vs__actions {
  cursor: pointer;
}

.vs__dropdown-menu {
  background: #ffffff;
  color: var(--ViCE---Dark-Grey, #606367);
  font-size: 14px;
  & .vs__dropdown-option--highlight {
    background-color: rgba(45, 212, 191, var(--tw-bg-opacity));
  }

  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 6px 0 6px 0;
  margin-top: 2px;
  position: relative;
  z-index: 9999;
  opacity: 95%;
}

.vs__dropdown-menu .vs__dropdown-option {
  padding: 0.25rem 1rem;
  border-radius: 6px;
  margin: 0 6px;
  cursor: pointer;
}

.bg-vice-teal-150 {
  background-color: #e6fcf5;
}

.vs--searchable .vs__dropdown-toggle {
  width: 100%;
  border-radius: 0.375rem;
  --tw-border-opacity: 1;
  height: 38px;
}

.vs--open .vs__dropdown-toggle {
  border-bottom-color: #d1d5db;
  font-size: 14px;
  color: rgb(136, 139, 136) !important;
}

.add-button {
  height: 38px;
  margin-top: 4px;
}
</style>
